import React, { useState } from 'react';
import type { Web20Article } from '../../../services/web2Service';
import { updateWeb20ArticleContent } from '../../../services/web2Service';

interface Props {
  article: Web20Article;
  onClose: () => void;
  onSaved?: (updated: Web20Article) => void;
  darkMode?: boolean;
}

export default function ArticleDrawer({ article, onClose, onSaved, darkMode }: Props) {
  const dm = darkMode;
  const [copied, setCopied] = useState<'title' | 'content' | null>(null);

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(article.title);
  const [editContent, setEditContent] = useState(article.content);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const cleanMarkdown = (text: string) => {
    return text.replace(/[#*]/g, '').replace(/^[ \t]+/gm, '').trim();
  };

  const copy = (text: string, type: 'title' | 'content') => {
    const textToCopy = type === 'content' ? cleanMarkdown(text) : text;
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await updateWeb20ArticleContent(article.id, {
        title: editTitle,
        content: editContent,
      });
      onSaved?.(updated);
      setIsEditing(false);
    } catch (e: any) {
      setSaveError(e?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditTitle(article.title);
    setEditContent(article.content);
    setSaveError(null);
    setIsEditing(false);
  };

  const bg = dm ? 'bg-gray-900 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200';
  const codeBg = dm ? 'bg-gray-950 text-gray-200' : 'bg-gray-50 text-gray-800';
  const subText = dm ? 'text-gray-400' : 'text-gray-500';

  // Display title — use editTitle if editing, otherwise article.title
  const displayTitle = isEditing ? editTitle : article.title;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer panel */}
      <div className={`relative w-full max-w-2xl h-full flex flex-col border-l shadow-2xl overflow-hidden ${bg}`}>
        {/* Header */}
        <div className={`flex-shrink-0 px-6 py-4 border-b flex items-start gap-3 ${dm ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold leading-snug">{displayTitle}</h2>
            <div className={`flex gap-4 mt-1 text-xs ${subText}`}>
              <span>{article.word_count} words</span>
              {article.uniqueness_score != null && <span>{Math.round(article.uniqueness_score * 100)}% unique</span>}
              {article.backlink_url && <span className="truncate max-w-[200px]">↗ {article.backlink_url}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!isEditing && (
              <button
                onClick={() => { setEditTitle(article.title); setEditContent(article.content); setIsEditing(true); }}
                className={`p-2 rounded-lg transition-colors ${dm ? 'hover:bg-gray-700 text-gray-400 hover:text-blue-400' : 'hover:bg-gray-200 text-gray-500 hover:text-blue-600'}`}
                title="Edit Article"
              >
                <i className="ri-pencil-line text-lg" />
              </button>
            )}
            <button onClick={onClose} className={`p-2 rounded-lg transition-colors ${dm ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}>
              <i className="ri-close-line text-xl" />
            </button>
          </div>
        </div>

        {/* Toolbar — only shown in view mode */}
        {!isEditing && (
          <div className={`flex-shrink-0 px-6 py-3 border-b flex flex-wrap gap-2 ${dm ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              onClick={() => copy(article.title, 'title')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${dm ? 'border-gray-600 hover:bg-gray-700 text-gray-300' : 'border-gray-200 hover:bg-gray-100 text-gray-700'}`}>
              <i className={copied === 'title' ? 'ri-check-line text-emerald-500' : 'ri-clipboard-line'} />
              {copied === 'title' ? 'Title Copied!' : 'Copy Title'}
            </button>
            <button
              onClick={() => copy(article.content, 'content')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${dm ? 'border-gray-600 hover:bg-gray-700 text-gray-300' : 'border-gray-200 hover:bg-gray-100 text-gray-700'}`}>
              <i className={copied === 'content' ? 'ri-check-line text-emerald-500' : 'ri-file-copy-line'} />
              {copied === 'content' ? 'Content Copied!' : 'Copy Full Content'}
            </button>
            {article.platform_url && (
              <a href={article.platform_url} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors">
                <i className="ri-external-link-line" />Open Platform
              </a>
            )}
          </div>
        )}

        {/* Edit toolbar — only shown in edit mode */}
        {isEditing && (
          <div className={`flex-shrink-0 px-6 py-3 border-b flex items-center gap-2 ${dm ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-blue-50/50'}`}>
            <i className="ri-edit-2-line text-blue-500 text-sm" />
            <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 flex-1">Editing article — changes will be saved to the database</span>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white border-none cursor-pointer transition-colors"
            >
              {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-3-line" />}
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button
              onClick={handleCancelEdit}
              disabled={saving}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border-none cursor-pointer transition-colors ${dm ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Error banner */}
        {saveError && (
          <div className="flex-shrink-0 px-6 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
            <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1.5">
              <i className="ri-error-warning-line" /> {saveError}
            </p>
          </div>
        )}

        {/* Content area */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Title</label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg text-sm font-bold focus:outline-none focus:ring-2 focus:ring-blue-500 ${dm ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Content</label>
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  rows={20}
                  className={`w-full px-3 py-2 border rounded-lg text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y ${dm ? 'bg-gray-800 border-gray-600 text-gray-200' : 'bg-white border-gray-200 text-gray-800'}`}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {article.image_url && (
                <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
                  <img
                    src={article.image_url}
                    alt={article.title}
                    className="w-full h-52 sm:h-64 object-cover"
                  />
                </div>
              )}
              <pre className={`whitespace-pre-wrap text-sm leading-relaxed font-sans rounded-xl p-4 ${codeBg}`}>
                {cleanMarkdown(article.content)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
